import { Md5 } from 'ts-md5';
import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';

import { UserRole } from '../Store/UserState';
import { normalizeCorrectionBlocks } from '../Util/oneClickCorrections';
import { hasAnyRole } from '../Util/UserUtils';

const ENDING_REGEX = /\s+$/gm;

export type LLMError = {
    type: string;
    errorcode: 'replace' | 'insert' | 'remove' | 'trace';
    offset: number;
    length: number;
    proposals: string[];
}

export type CorrectionBlock = { type: LLMError['errorcode'] | 'common'; copy: string };

export type Segment = {
    id: string;
    beginOffset: number;
    endOffset: number;
    currentText?: string;
    text: string;
    originText?: string;
    blocks?: CorrectionBlock[];
    traceBlocks?: CorrectionBlock[];
    isSkipped?: boolean;
    error?: boolean;
    corrections?: LLMError[];
    issue?: {
        code?: string;
        title?: string;
        message?: string;
    }
};

export type OneClickStore = {
    isOneClickModeActive: boolean;
    charsRemaining?: number;
    setIsOneClickModeActive: (value: boolean) => void;
    isRunCheckActivated: boolean,
    setIsRunCheckActivated: (value: boolean) => void;
    text: string;
    segments: Segment[];
    setSegment: (segmentId: string, segmentData: Partial<Segment>) => void;
    removeSegment: (segmentId: string) => void;
    setSegments: (segments: Segment[]) => void;
    editorNode: HTMLDivElement | null;
    cleanEditorNode: () => void;
    setEditorNode: (node: HTMLDivElement | null) => void;
    handleSegmentize: () => Promise<void>;
    handleCorrection: () => Promise<void>;
    handleSegmentCorrection: (id: string) => Promise<boolean>;
    segmentsLoading: Record<number, boolean>;
    isSegmentLoadding: (id: number) => boolean;
    isSegmentizeLoadding: boolean;
    isSegmentizeSilentLoadding: boolean;
    singleCorrectionActiveIndex: number;
    // handleSetSingleCorrectionMode: (value?: boolean) => void;
    handleSetSingleCorrectionActiveIndex: (props: { id?: string; value?: number; shiftValue?: number }) => void;
    isOneClickAgreed: boolean;
    setOneClickAgreed: (value: boolean) => void;
    bufferText: string;
    setBufferText: (text: string) => void;
    syncBufferText: () => void;
    summary: {
        plan?: 'premium' | 'basic',
        limit?: number,
    },
    hashes: {
        userHash?: '',
        roleHash?: '',
    };
    setHashes: (email: string, roles: UserRole[]) => void;
};

const useOneClickStore = create<OneClickStore>()(subscribeWithSelector(persist((set, get) => ({
    isOneClickModeActive: window.location.hash.includes('express'),
    setIsOneClickModeActive: (value) => set({ isOneClickModeActive: value }),
    isRunCheckActivated: false,
    setIsRunCheckActivated: (value) => set({ isRunCheckActivated: value }),
    text: '',
    segments: [],
    setSegment: (segmentId, segmentData) => set((state: OneClickStore) => ({
        segments: state.segments.map((segment: Segment) => {
            if (segment.id === segmentId) {
                return { ...segment, ...segmentData };
            }

            return segment;
        })
    })),
    removeSegment: (segmentId) => set((state: OneClickStore) => ({
        segments: state.segments.filter((segment) => segment.id !== segmentId)
    })),
    setSegments: (items) => set({ segments: items }),
    segmentsLoading: {},
    isSegmentLoadding: (id) => !!get().segmentsLoading[id],
    isSegmentizeLoadding: false,
    isSegmentizeSilentLoadding: false,
    editorNode: null,
    cleanEditorNode: () => set(({ editorNode }: OneClickStore) => {
        if (editorNode) editorNode.innerHTML = '';

        return {
            text: '',
            bufferText: '',
            segments: [],
            singleCorrectionActiveIndex: -1
        };
    }),
    setEditorNode: (node) => set({ editorNode: node }),
    handleSegmentize: async () => {
        const currentText = get().editorNode?.innerText;

        if (!currentText) return;

        try {
            set({ isSegmentizeLoadding: true, isSegmentizeSilentLoadding: true, singleCorrectionActiveIndex: -1 });

            setTimeout(() => {
                set({ isSegmentizeSilentLoadding: false });
            }, 2000);

            const response = await fetch('/segmentize', {
                method: 'POST',
                cache: 'no-cache',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({
                    text: currentText,
                })
            });

            const responseData = await response.json();
            const segments: Omit<Segment, 'id'>[] = responseData.result;

            // clean up editor content
            const editorNode = get().editorNode;
            if (editorNode) {
                editorNode.innerHTML = '';
            }

            const currentSegments: Segment[] = [...get().segments];

            set({ segments: [] }); // TODO

            const normalizedSegments = segments.map((segment) => {
                const id = `segment-${segment.beginOffset}`;

                const existingSegment = currentSegments.find((currentSegment) => {
                    const actualText = currentSegment.currentText ?? currentSegment.text;
                    return actualText.trim() === segment.text?.trim();
                });

                if (existingSegment) {
                    return {
                        ...existingSegment,
                        originText: segment.text,
                        text: segment.text,
                        currentText: segment.text,
                        ...(segment.text !== existingSegment.text && {
                            blocks: normalizeCorrectionBlocks(segment.text, existingSegment.corrections),
                            traceBlocks: normalizeCorrectionBlocks(segment.text, existingSegment.corrections, { showTraces: true }),
                        }),
                        id,
                    };
                }

                return { ...segment, id };
            });

            return set({
                segments: normalizedSegments,
                isSegmentizeLoadding: false
            });
        } catch (err) {
            console.error('handleSegmentize ', err);
            set({ isSegmentizeLoadding: false });
            return;
        }
    },
    handleCorrection: async () => {
        const { isSegmentizeLoadding, isSegmentizeSilentLoadding } = get();

        if (isSegmentizeLoadding || isSegmentizeSilentLoadding) return;

        await get().handleSegmentize();

        const segmentsLoading = {};
        const filteredSegments = (get() as OneClickStore).segments
            .filter((segment) => {
                const isNotCorrected = !segment.currentText || segment.currentText !== segment.text;

                if (isNotCorrected) {
                    segmentsLoading[segment.id] = true;
                }

                return isNotCorrected;
            });

        set({
            segmentsLoading
        });

        for (const segment of filteredSegments) {
            if (!segment.currentText || segment.currentText !== segment.text) {
                if (!get().isOneClickModeActive) break;

                const wasCorrected = await get().handleSegmentCorrection(segment.id);

                if (!wasCorrected) {
                    set((store: OneClickStore) => ({
                            segmentsLoading: {},
                            // singleCorrectionActiveIndex: store.segments.findIndex((s: Segment) => s.id === segment.id)
                        }
                    ));
                    break;
                }
            }
        }
    },
    handleSegmentCorrection: async (id) => {
        const activeSegment = get().segments.find((segment: Segment) => segment.id === id);

        if (!activeSegment) return false;

        const originText = activeSegment.currentText || activeSegment.text;

        try {
            set({ segmentsLoading: { ...get().segmentsLoading, [activeSegment.id]: true } });
            const response = await fetch('/apigateway/llm_diff', {
                method: 'POST',
                credentials: 'include',
                cache: 'no-cache',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({
                    text: originText,
                    ...get().hashes
                })
            });

            const responseData: {
                checkResults?: { text: string, errors: LLMError[], code?: string },
                messages?: { message: string, shortMessage: string },
                charsRemaining?: number,
                charsTotal?: number,
                error?: string,
            } = await response.json();

            const { text, errors, code } = responseData.checkResults ?? {};
            const { message, shortMessage } = responseData.messages ?? {};

            let resultText = text ?? '';
            const hasIssue = !!(message || shortMessage);
            const hasError = !!responseData.error || !responseData.charsRemaining;

            const originTextRightSpace = originText.search(ENDING_REGEX);
            const resultTextRightSpace = resultText.search(ENDING_REGEX);

            // keep spacing from origin text
            if (originTextRightSpace !== resultTextRightSpace && originTextRightSpace !== -1) {
                resultText = (resultTextRightSpace === -1 ? resultText : resultText?.substring(0, resultTextRightSpace)) + originText.substring(originTextRightSpace);
            }

            if (hasIssue && !resultText) {
                resultText = originText;
            }

            const blocks = normalizeCorrectionBlocks(resultText, errors);
            const traceBlocks = normalizeCorrectionBlocks(resultText, errors, { showTraces: true });

            set((store: OneClickStore) => ({
                summary: { ...store.summary, limit: responseData.charsTotal },
                charsRemaining: hasError ? 0 : (responseData.charsRemaining ?? store.charsRemaining ?? 0),
                segments: store.segments.map((segment: Segment) => {
                    if (segment.id === id) {
                        return {
                            ...segment,
                            text: resultText,
                            originText,
                            currentText: resultText,
                            blocks,
                            traceBlocks,
                            isSkipped: false,
                            corrections: errors,
                            ...(hasIssue && {
                                issue: {
                                    code,
                                    title: shortMessage,
                                    message
                                },
                            }),
                            ...(hasError && {
                                error: responseData.error || 'Error',
                                text: originText,
                                blocks: [],
                                traceBlocks: [],
                            })
                        };
                    }

                    return segment;
                }),
                segmentsLoading: { ...store.segmentsLoading, [activeSegment.id]: false }
            }));

            return !hasError;
        } catch (err) {
            console.error('handleSegmentize ', err);
            set({ segmentsLoading: { ...get().segmentsLoading, [activeSegment.id]: false } });
            return false;
        }

    },
    singleCorrectionActiveIndex: -1,
    handleSetSingleCorrectionActiveIndex: ({ id, value, shiftValue }) => set((store: OneClickStore) => {
        if (id) {
            const currentIndex = store.segments.findIndex((segment: Segment) => segment.id === id);

            if (currentIndex >= 0) {
                return {
                    singleCorrectionActiveIndex: value
                };
            }

            return;
        }

        if (typeof value === "number") {
            return {
                singleCorrectionActiveIndex: value
            };
        }

        if (typeof shiftValue === "number") {
            const currentIndex = store.singleCorrectionActiveIndex;
            const newIndex = currentIndex + shiftValue;

            return {
                singleCorrectionActiveIndex: Math.max(0, Math.min(store.segments.length - 1, newIndex))
            };
        }

        return;
    }),
    handleSingleSegmentCorrection: async () => {
        const { singleCorrectionActiveIndex, segments, handleSegmentCorrection } = get();
        const activeSgment = segments[singleCorrectionActiveIndex];

        if (activeSgment?.id) {
            await handleSegmentCorrection(activeSgment?.id);
        }
    },
    isOneClickAgreed: false,
    setOneClickAgreed: (value) => set({ isOneClickAgreed: value }),
    bufferText: "",
    setBufferText: (text) => set({ bufferText: text.trim() }),
    syncBufferText: () => set((state: OneClickStore) => ({ text: state.bufferText })),
    summary: {},
    hashes: {},
    setHashes: (email, roles = []) => {
        if (!email) return;

        const userHash = Md5.hashStr(email);
        const isPremium = hasAnyRole(roles, UserRole.Premium, UserRole.Premium20, UserRole.B2B_Licensemanager_Premium20, UserRole.B2B_Licensee_Premium20, UserRole.B2B_Licensemanager_charity_Premium, UserRole.B2B_Licensee_charity_Premium);
        const plan = isPremium ? 'premium' : 'basic';
        const roleHash = Md5.hashStr(`${userHash}${plan}`);

        return set((store: OneClickStore) => ({
            summary: {
                ...store.summary,
                plan,
            },
            hashes: {
                userHash,
                roleHash
            }
        }));
    }
}), {
    name: 'OneClickText',
    storage: createJSONStorage(() => sessionStorage),
    partialize: (state) => ({
        isOneClickAgreed: state.isOneClickAgreed,
        bufferText: state.bufferText,
    })
})));

useOneClickStore.subscribe(
    (store: OneClickStore) => store.isOneClickModeActive,
    (isOneClickModeActive: boolean) => {
        const newUrl = new URL(window.location.href);

        newUrl.hash = isOneClickModeActive ? 'express' : '';
        // isOneClickModeActive ? newUrl.searchParams.set('express', 'true') : newUrl.searchParams.delete('express');

        window.history.replaceState(
            {},
            '',
            newUrl.toString());
    });

export const selectIsSegmentsLoading = (store: OneClickStore) => store.isSegmentizeLoadding || store.isSegmentizeSilentLoadding || Object.values(store.segmentsLoading).some(segmentId => segmentId);

export const selectOneClickPercentage = (store: OneClickStore) => {
    const segmentsCount = store.segments.length;
    const isSegmentizeLoadding = store.isSegmentizeLoadding;

    if (isSegmentizeLoadding) {
        return 0;
    }

    if (segmentsCount) {
        const readySegmentsCount = store.segments.filter(segment => segment.currentText === segment.text).length;

        return readySegmentsCount / segmentsCount * 100;
    }

    return 0;
};

export default useOneClickStore;